import React from 'react';

import { PersonalisationProgressBar } from '../PersonalisationProgressBar';
import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { getStepCount } from '../../utilities/getStepCount';

import styles from './personalisation-header.module.css';

/**
 * PersonalisationHeader component displays the header for the personalisation journey, including title, reset, and close actions.
 *
 * Two designs, picked per Personalisation by `headerStyle`: V1 (the default) is the title with
 * Reset and close beside it and the progress dots below; V2 is a Back/close bar over a
 * "Step n/m" and Reset row, then the title, with no dots.
 *
 * @param {Object} props - Component props
 * @param {string} props.title - The header title
 * @param {string} props.resetText - The reset button text
 * @param {function} props.onClose - Callback for closing the modal
 * @param {function} props.onReset - Callback for resetting the journey
 * @param {function} [props.onBack] - Callback for the V2 Back button
 * @param {number} props.totalSteps - Total number of steps
 * @param {number} props.currentStep - Current step index
 * @param {boolean} props.isResetting - Whether the header is in resetting state
 * @param {'V1'|'V2'} [props.headerStyle] - Which header design to render (V1 when omitted)
 * @param {string} [props.closeLabel] - Accessible name of the X button ('Close' when omitted)
 * @param {boolean} [props.showProgress] - V1 only: render the progress dots (true when omitted).
 *   Hiding them also drops the space the title row keeps for them.
 * @param {boolean} [props.disabled] - Disable Back, Reset and X (false when omitted)
 * @returns {import('react').JSX.Element}
 */
export function PersonalisationHeader({
  title,
  resetText,
  onClose,
  onReset,
  onBack,
  totalSteps,
  currentStep,
  isResetting,
  headerStyle,
  closeLabel = 'Close',
  showProgress = true,
  disabled = false
}) {
  // PersonalisationHeader now directly uses context
  const { hasPersonalisationOptions, personalisationSteps } = usePersonalisationContext();

  if (headerStyle === 'V2') {
    // Same numbers as the V1 "Step X of Y" under the dots, and hidden in the same case: a
    // single-step journey has no dots and no counter.
    const stepCount = getStepCount({ currentStep, totalSteps: personalisationSteps.size });
    const showStepCount = stepCount.totalSteps > 1;
    const showReset = resetText && hasPersonalisationOptions;

    return (
      <div className={styles['personalisation__header-v2']}>
        <div className={styles['personalisation__header-bar']}>
          <button
            type="button"
            className={styles['personalisation__header-back'] + ' body-2xs font-medium button-reset'}
            onClick={(e) => onBack(e)}
            disabled={disabled}
          >
            <span className="svg-wrapper" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
                <path
                  d="M12.5 4.5L7 10L12.5 15.5"
                  stroke="currentColor"
                  strokeWidth="0.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            Back
          </button>
          {onClose && (
            <button
              type="button"
              className="svg-wrapper button-reset"
              onClick={(e) => onClose(e)}
              aria-label={closeLabel}
              disabled={disabled}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M5.35404 4.64645C5.15878 4.45118 4.8422 4.45118 4.64694 4.64645C4.45167 4.84171 4.45167 5.15829 4.64693 5.35355L11.2934 12L4.64693 18.6465C4.45167 18.8417 4.45167 19.1583 4.64694 19.3536C4.8422 19.5488 5.15878 19.5488 5.35404 19.3536L12.0005 12.7071L18.6469 19.3536C18.8422 19.5488 19.1588 19.5488 19.3541 19.3536C19.5493 19.1583 19.5493 18.8417 19.3541 18.6465L12.7076 12L19.3541 5.35355C19.5493 5.15829 19.5493 4.84171 19.3541 4.64645C19.1588 4.45118 18.8422 4.45118 18.6469 4.64645L12.0005 11.2929L5.35404 4.64645Z"
                  fill="#171717"
                />
              </svg>
            </button>
          )}
        </div>
        <div className={styles['personalisation__header-title-bar']}>
          {(showStepCount || showReset) && (
            <div className={styles['personalisation__header-step-row']}>
              {/* role="status" announces the position as the customer moves between steps, as the V1 counter does. */}
              {showStepCount && (
                <p className="body-2xs font-medium" role="status">
                  {`Step ${stepCount.currentStepNumber}/${stepCount.totalSteps}`}
                </p>
              )}
              {showReset && (
                <button
                  type="button"
                  className={styles['personalisation__header-reset-link'] + ' body-2xs font-medium button-reset'}
                  onClick={(e) => onReset(e)}
                  disabled={disabled}
                >
                  {resetText}
                </button>
              )}
            </div>
          )}
          {title && hasPersonalisationOptions && <h2 className="heading-2xl">{title}</h2>}
        </div>
      </div>
    );
  }

  return (
    <div className={styles['personalisation__header']}>
      <div
        className={
          styles['personalisation__header-content'] +
          (showProgress ? '' : ' ' + styles['personalisation__header-content--no-progress'])
        }
      >
        {title && hasPersonalisationOptions && <h2 className="heading-2xl">{title}</h2>}
        <div className={styles['personalisation__header-actions']}>
          {resetText && hasPersonalisationOptions && (
            <button
              type="button"
              className={'body-sm button-reset ' + styles['personalisation__header-reset']}
              onClick={(e) => onReset(e)}
              disabled={disabled}
            >
              {resetText}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className={styles['personalisation__header-close'] + ' svg-wrapper button-reset'}
              onClick={(e) => onClose(e)}
              aria-label={closeLabel}
              disabled={disabled}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M5.35404 4.64645C5.15878 4.45118 4.8422 4.45118 4.64694 4.64645C4.45167 4.84171 4.45167 5.15829 4.64693 5.35355L11.2934 12L4.64693 18.6465C4.45167 18.8417 4.45167 19.1583 4.64694 19.3536C4.8422 19.5488 5.15878 19.5488 5.35404 19.3536L12.0005 12.7071L18.6469 19.3536C18.8422 19.5488 19.1588 19.5488 19.3541 19.3536C19.5493 19.1583 19.5493 18.8417 19.3541 18.6465L12.7076 12L19.3541 5.35355C19.5493 5.15829 19.5493 4.84171 19.3541 4.64645C19.1588 4.45118 18.8422 4.45118 18.6469 4.64645L12.0005 11.2929L5.35404 4.64645Z"
                  fill="#171717"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
      {showProgress && (
        <PersonalisationProgressBar totalSteps={totalSteps} currentStep={currentStep} isResetting={isResetting} />
      )}
    </div>
  );
}
